<script setup lang="ts">
import { computed, ref } from "vue";

import AppModal from "@shared/ui/components/AppModal.vue";
import IconArrowRight from "@shared/ui/icons/IconArrowRight.vue";
import IconEdit from "@shared/ui/icons/IconEdit.vue";
import IconGrid from "@shared/ui/icons/IconGrid.vue";
import IconShuffle from "@shared/ui/icons/IconShuffle.vue";

import UserAvatar from "@entities/user/ui/UserAvatar.vue";

const NAME_MAX = 24;

// Ник вводят по-русски, а хэндл живёт в ссылках (monster://…) — поэтому
// латинизируем, а не просто вырезаем не-ASCII.
const TRANSLIT: Record<string, string> = {
    а: "a",
    б: "b",
    в: "v",
    г: "g",
    д: "d",
    е: "e",
    ё: "e",
    ж: "zh",
    з: "z",
    и: "i",
    й: "y",
    к: "k",
    л: "l",
    м: "m",
    н: "n",
    о: "o",
    п: "p",
    р: "r",
    с: "s",
    т: "t",
    у: "u",
    ф: "f",
    х: "h",
    ц: "c",
    ч: "ch",
    ш: "sh",
    щ: "sch",
    ъ: "",
    ы: "y",
    ь: "",
    э: "e",
    ю: "yu",
    я: "ya",
};

const name = defineModel<string>("name", { required: true });
const avatar = defineModel<string>("avatar", { required: true });

const props = defineProps<{ avatars: string[] }>();

const emit = defineEmits<{ next: []; link: [] }>();

const pickerOpen = ref(false);

const handle = computed(() => {
    const slug = name.value
        .toLowerCase()
        .split("")
        .map((char) => TRANSLIT[char] ?? char)
        .join("")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    return slug || "monster";
});

const canStart = computed(() => !!name.value.trim());

function avatarTitle(key: string): string {
    return key.split("/").pop()?.replace(".svg", "") ?? key;
}

function shuffle(): void {
    const others = props.avatars.filter((key) => key !== avatar.value);
    if (!others.length) {
        return;
    }
    avatar.value = others[Math.floor(Math.random() * others.length)];
}

function pick(key: string): void {
    avatar.value = key;
    pickerOpen.value = false;
}
</script>

<template>
    <section class="mc-identity">
        <div class="mc-identity__badge">
            <span class="mc-identity__badge-dash" />
            Новый профиль · Шаг 1
        </div>

        <h1 class="mc-identity__title">
            Добро пожаловать в<br />
            <span class="mc-identity__title-accent">Monster Chat</span>
        </h1>

        <p class="mc-identity__lead">
            Выбери ник и облик. Твоя личность хранится только на этом устройстве
            — без почты, без телефона, без сервера, который может тебя
            прочитать.
        </p>

        <div class="mc-identity__field">
            <label class="mc-identity__label">Твой аватар</label>
            <div class="mc-identity__avatar-row">
                <button
                    class="mc-identity__avatar"
                    title="Сменить аватар"
                    @click="pickerOpen = true"
                >
                    <UserAvatar :avatar-key="avatar" :size="56" />
                    <span class="mc-identity__avatar-edit">
                        <IconEdit />
                    </span>
                </button>

                <button class="mc-identity__outline" @click="pickerOpen = true">
                    <IconGrid />
                    Выбрать из {{ avatars.length }}
                </button>

                <button
                    class="mc-identity__outline mc-identity__outline_icon"
                    title="Случайный"
                    @click="shuffle"
                >
                    <IconShuffle />
                </button>
            </div>
        </div>

        <div class="mc-identity__field">
            <div class="mc-identity__label-row">
                <label class="mc-identity__label">Твоё имя</label>
                <span
                    class="mc-identity__counter"
                    :class="{
                        'mc-identity__counter_full': name.length === NAME_MAX,
                    }"
                >
                    {{ name.length }}/{{ NAME_MAX }}
                </span>
            </div>
            <div class="mc-identity__input">
                <input
                    v-model="name"
                    :maxlength="NAME_MAX"
                    placeholder="напр. void_rat"
                    @keydown.enter="canStart && emit('next')"
                />
                <span class="mc-identity__handle">@{{ handle }}</span>
            </div>
        </div>

        <div class="mc-identity__actions">
            <button
                class="mc-identity__cta"
                :disabled="!canStart"
                @click="emit('next')"
            >
                Начать
                <IconArrowRight />
            </button>

            <!-- Второй сценарий: не создавать аккаунт, а привязать это
                 устройство к уже существующему (модель A). -->
            <button class="mc-identity__link" @click="emit('link')">
                Уже есть аккаунт? Привязать это устройство
            </button>
        </div>
    </section>

    <AppModal
        title="Выбери аватар"
        :max-width="560"
        :is-visible="pickerOpen"
        @close="pickerOpen = false"
    >
        <div class="mc-identity__picker-hint">
            {{ avatars.length }} монстров · выбери одного
        </div>
        <div class="mc-identity__picker">
            <button
                v-for="key in avatars"
                :key="key"
                class="mc-identity__tile"
                :class="{ 'mc-identity__tile_active': key === avatar }"
                :title="avatarTitle(key)"
                @click="pick(key)"
            >
                <UserAvatar :avatar-key="key" :size="56" />
            </button>
        </div>
    </AppModal>
</template>

<style lang="scss" scoped>
.mc-identity {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 26px;

    &__badge {
        display: flex;
        align-items: center;
        gap: 10px;
        font-family: var(--mc-mono);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: var(--mc-acid);
    }

    &__badge-dash {
        width: 20px;
        height: 1px;
        background: var(--mc-acid);
    }

    &__title {
        margin: -14px 0 0;
        font-size: 40px;
        font-weight: 700;
        letter-spacing: -0.03em;
        line-height: 1.22;

        @media (max-width: bp.$mobile) {
            font-size: 30px;
        }
    }

    &__title-accent {
        color: var(--mc-acid);
        text-shadow: 0 0 40px var(--mc-acid-glow-strong);
    }

    &__lead {
        margin: -16px 0 0;
        font-size: 14px;
        font-weight: 500;
        line-height: 1.55;
        color: var(--mc-fg-mute);
    }

    &__field {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    &__label-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    &__label {
        font-family: var(--mc-mono);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--mc-fg-dim);
    }

    &__counter {
        font-family: var(--mc-mono);
        font-size: 10px;
        font-weight: 600;
        color: var(--mc-fg-dim);

        &_full {
            color: var(--mc-warn);
        }
    }

    &__avatar-row {
        display: flex;
        align-items: center;
        gap: 14px;
    }

    &__avatar {
        position: relative;
        flex-shrink: 0;
        line-height: 0;

        &:hover {
            filter: brightness(1.06);
        }
    }

    &__avatar-edit {
        position: absolute;
        right: -6px;
        bottom: -6px;
        width: 22px;
        height: 22px;
        display: grid;
        place-items: center;
        background: var(--mc-bg-window);
        border: 1px solid var(--mc-acid);
        color: var(--mc-acid);

        svg {
            width: 12px;
            height: 12px;
        }
    }

    &__outline {
        display: flex;
        align-items: center;
        gap: 9px;
        height: 44px;
        padding: 0 16px;
        border: 1px solid var(--mc-line-hard);
        color: var(--mc-fg);
        font-size: 13px;
        font-weight: 600;
        transition: 0.1s;

        &:hover {
            border-color: var(--mc-acid);
            color: var(--mc-acid);
        }

        &_icon {
            width: 44px;
            padding: 0;
            justify-content: center;
            color: var(--mc-fg-mute);
        }
    }

    &__input {
        display: flex;
        align-items: center;
        gap: 10px;
        height: 52px;
        padding: 0 14px;
        background: var(--mc-bg-input);
        border: 1px solid var(--mc-line-hard);
        transition: 0.15s;

        &:focus-within {
            border-color: var(--mc-acid);
            box-shadow: 0 0 24px var(--mc-acid-glow);
        }

        input {
            flex: 1;
            min-width: 0;
            height: 100%;
            border: none;
            outline: none;
            background: transparent;
            color: var(--mc-fg);
            font-size: 16px;
            font-weight: 600;
            padding: 0;
        }
    }

    &__handle {
        font-family: var(--mc-mono);
        font-size: 12px;
        font-weight: 600;
        color: var(--mc-fg-dim);
        white-space: nowrap;
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
    }

    &__cta {
        display: flex;
        align-items: center;
        gap: 10px;
        height: 48px;
        padding: 0 22px;
        background: var(--mc-acid);
        color: var(--mc-fg-message);
        font-size: 15px;
        font-weight: 700;
        letter-spacing: -0.01em;
        box-shadow: 0 0 24px var(--mc-acid-glow);
        transition: box-shadow 0.15s;

        &:hover {
            box-shadow: 0 0 40px var(--mc-acid-glow-strong);
        }

        &:disabled {
            opacity: 0.4;
            pointer-events: none;
        }

        svg {
            width: 18px;
            height: 18px;
        }
    }

    &__link {
        font-family: var(--mc-mono);
        font-size: 12px;
        font-weight: 600;
        color: var(--mc-acid);
        padding-bottom: 2px;
        border-bottom: 1px solid var(--mc-acid-glow-strong);

        &:hover {
            color: var(--mc-fg);
            border-bottom-color: var(--mc-fg);
        }
    }

    &__picker-hint {
        font-family: var(--mc-mono);
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.03em;
        color: var(--mc-fg-mute);
        margin-bottom: 14px;
    }

    &__picker {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));
        gap: 8px;
    }

    &__tile {
        aspect-ratio: 1;
        display: grid;
        place-items: center;
        line-height: 0;

        &:hover {
            filter: brightness(1.06);
        }

        &_active {
            box-shadow:
                0 0 0 1px var(--mc-bg-window),
                0 0 0 3px var(--mc-acid),
                0 0 18px var(--mc-acid-glow);
        }
    }
}
</style>
